import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { config } from "../config";
import { getUserPreferences, setUserPreferences } from "../lib/user-preferences";

import type { JSX, ReactNode } from "react";

import type { Theme, ThemeContextType } from "../types";

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps): JSX.Element => {
  const [theme, setTheme] = useState<Theme>(() => {
    if (!config.features.darkMode) {
      return `light`;
    }

    const stored = getUserPreferences().theme;

    if (stored === `light` || stored === `dark` || stored === `auto`) {
      return stored;
    }

    if (config.ui.theme === `auto`) {
      return `auto`;
    }

    return config.ui.theme === `dark` ? `dark` : `light`;
  });

  useEffect(() => {
    const { documentElement: root } = window.document;

    root.classList.remove(`dark`);
    document.body.classList.remove(`dark`);

    const prefersDark = window.matchMedia(`(prefers-color-scheme: dark)`).matches;

    let useDark = false;

    if (theme === `auto`) {
      useDark = prefersDark;
    } else {
      useDark = theme === `dark`;
    }

    if (useDark) {
      root.classList.add(`dark`);
    }

    // Do not toggle color-scheme to avoid UA forcing dark backgrounds

    setUserPreferences({ theme });
  }, [theme]);

  useEffect(() => {
    if (!config.features.darkMode) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(prefers-color-scheme: dark)`);

    const handleChange = () => {
      const stored = getUserPreferences().theme;

      if (stored === `auto`) {
        setTheme(`auto`);
      }
    };

    mediaQuery.addEventListener(`change`, handleChange);

    return () => {
      mediaQuery.removeEventListener(`change`, handleChange);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      if (prev === `light`) {
        return `dark`;
      }

      if (prev === `dark`) {
        return `auto` as Theme;
      }

      return `light`;
    });
  }, []);

  const handleSetTheme = useCallback((newTheme: Theme) => {
    setTheme(newTheme);
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({
      theme,
      toggleTheme,
      setTheme: handleSetTheme,
    }),
    [theme, toggleTheme, handleSetTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error(`useTheme must be used within a ThemeProvider`);
  }

  return context;
}

export function useIsDark(): boolean {
  const { theme } = useTheme();
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const prefersDark = window.matchMedia(`(prefers-color-scheme: dark)`).matches;
    const next = theme === `dark` || (theme === `auto` && prefersDark);

    setIsDark(next);
  }, [theme]);

  return isDark;
}
